import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const HeaderWrapper = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: rgba(3, 4, 5, 0.8);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid ${props => props.theme.colors.border};
  z-index: 1000;
  display: flex;
  align-items: center;
  padding: 0 2rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0 1.25rem;
  }
`;

const Nav = styled.nav`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${props => props.theme.colors.text};
  font-weight: 700;
  cursor: pointer;
  
  .bracket {
    color: ${props => props.theme.colors.textDim};
  }
  
  .name {
    color: ${props => props.theme.colors.primary};
  }
`;

const Menu = styled.ul`
  display: flex;
  gap: 2rem;
  list-style: none;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

const MenuItem = styled(motion.li)`
  color: ${props => props.theme.colors.textDim};
  cursor: pointer;
  position: relative;
  
  &:hover {
    color: ${props => props.theme.colors.text};
  }
  
  &.active {
    color: ${props => props.theme.colors.primary};
  }
  
  &::before {
    content: '//';
    margin-right: 0.5rem;
    color: ${props => props.theme.colors.border};
    opacity: 0;
    transition: all 0.2s;
  }
  
  &:hover::before {
    opacity: 1;
    color: ${props => props.theme.colors.primary};
  }
`;

const SystemStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  color: ${props => props.theme.colors.textDim};
  font-size: 0.75rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: none;
  }
  
  .indicator {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${props => props.theme.colors.success};
      box-shadow: 0 0 8px ${props => props.theme.colors.success};
    }
  }
`;

const Header = () => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <HeaderWrapper
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "circOut" }}
    >
      <Nav>
        <Logo onClick={() => scrollTo('hero')}>
          <span className="bracket">&lt;</span>
          <span className="name">Yul.dev</span>
          <span className="bracket">/&gt;</span>
        </Logo>

        <Menu>
          {['About', 'Skills', 'Projects', 'Contact'].map((item) => (
            <MenuItem 
              key={item} 
              onClick={() => scrollTo(item.toLowerCase())}
              whileHover={{ x: 2 }}
            >
              {item}
            </MenuItem>
          ))}
        </Menu>

        <SystemStatus>
          <div className="indicator">
            <span className="dot" />
            <span>Best time to email me</span>
          </div>
          <div>
            {time.toLocaleTimeString('en-US', { hour12: false })}
          </div>
        </SystemStatus>
      </Nav>
    </HeaderWrapper>
  );
};

export default Header;
